import { RegularText } from '@Iot-Bee/standard-web-library';
import { styled } from '@mui/material/styles';
import dayjs, { Dayjs } from 'dayjs';
import html2canvas from 'html2canvas-pro';
import jsPDF from 'jspdf';
import { DashboardDeviceData } from './interfaces';
import { BarchartObject, SeriesData } from './components/charts/interfaces';
import { Device } from '../../Types/types';
import { GroupBy, TimeGroupingInterval, TimeRange } from './types';
import { noCacheFetcher } from '../../utils/fetch';
import { SensorData } from '@Iot-Bee/web-backend-library';
import { fills, insideOutsideFills } from './components/charts/themes';
import weekOfYear from 'dayjs/plugin/weekOfYear';

dayjs.extend(weekOfYear);

export const formatValueLabel = (value: number) => {
  const hours = Math.floor(value / 60);
  const minutes = value % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

export const PageSection = styled('section')(() => ({
  padding: '20px',
}));

export const SectionText = styled(RegularText)(() => ({
  marginBottom: '15px',
  marginTop: '1px',
}));

// Options for function below
const options = {
  async: true,
  foreignObjectRendering: false,
  logging: false,
};

export const exportChartAsPDF = (chartContainerId: string) => {
  const input = document.getElementById(chartContainerId);
  if (input) {
    html2canvas(input, options).then((canvas) => {
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'landscape', // Adjust the orientation based on your chart layout
        unit: 'px',
        format: [canvas.width, canvas.height],
      });

      pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
      pdf.save('kwh-consumption.pdf');
    });
  }
};

export const exportDataAsCSV = (
  data: BarchartObject,
  selectedDevices: string[],
  workingHoursRange: number[],
  fileName: string
) => {
  if (Object.keys(data).length === 0) return; // Early return if no data

  // Define headers for the new CSV structure
  const headers = ['Date', 'kWh', 'Tag', 'Selected Devices', 'Working Hours Start', 'Working Hours End'];

  const dates = data.categories;

  // Convert data to CSV format, including the new structure
  const csvContent = [
    headers.join(','), // Header row (only once)
    dates
      .map((date, index) => {
        return data.series
          .map((series) => {
            const total = index < series.data.length ? series.data[index].toFixed(2) : 0;
            const tag = series.name;
            const selectedDevicesString = `"${selectedDevices.join(' ')}"`;
            const workingHoursStart = formatValueLabel(workingHoursRange[0]);
            const workingHoursEnd = formatValueLabel(workingHoursRange[1]);
            return [date, total, tag, selectedDevicesString, workingHoursStart, workingHoursEnd].join(',');
          })
          .join('\n');
      })
      .join('\n'),
  ].join('\n');

  // Create a Blob and download it
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  link.style.display = 'none';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export function generateFriendlyNamesMap(newDevices: Device[]): Map<string, string> {
  const friendlyNameIdMap = new Map<string, string>();
  newDevices.forEach((device) => {
    const value = device.deviceName && device.deviceName.trim() !== '' ? device.deviceName : device.id;
    friendlyNameIdMap.set(device.id, value);
  });
  return friendlyNameIdMap;
}

export function formatDateToMySQLTimestamp(date: Dayjs): string {
  const year = date.format('YYYY');
  const month = date.format('MM');
  const day = date.format('DD');
  const hours = date.format('HH');
  const minutes = date.format('mm');
  const seconds = date.format('ss');

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

// Function to convert a timestamp to minutes since midnight (local time)
export function timestampToMinutes(timestamp: Date): number {
  const time = dayjs(timestamp);
  return time.hour() * 60 + time.minute();
}

export async function fetchDashboardData(
  fromDate: Dayjs,
  toDate: Dayjs,
  devices: Device[]
): Promise<DashboardDeviceData[]> {
  if (devices.length > 0) {
    const url =
      process.env.REACT_APP_API_URL +
      `getdashboardkwh/?from=${formatDateToMySQLTimestamp(fromDate.utc())}&to=${formatDateToMySQLTimestamp(toDate.utc())}
    &label=${encodeURIComponent('kwh_delta_sum')}`;

    try {
      const response = await noCacheFetcher(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (response) {
        const data: SensorData[] = await response.json();
        data.forEach((item) => {
          item.time = dayjs.utc(item.time).local().toDate(); // Convert to local time
        });

        data.sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());

        const deviceWithData = data.reduce(
          (acc, curr) => {
            const device = devices.find((dev) => dev.id === curr.deviceId);
            if (device) {
              if (!acc[curr.deviceId]) {
                const tags =
                  device.tags === null || device.tags === undefined || device.tags === '' ? 'Untagged' : device.tags;

                acc[curr.deviceId] = {
                  sensorId: curr.deviceId,
                  data: [],
                  tags: [tags],
                };
              }

              acc[curr.deviceId].data.push({
                timestamp: curr.time,
                value: Number(curr.value.toFixed(3)),
              });
            }
            return acc;
          },
          {} as Record<string, DashboardDeviceData>
        );
        const devicesArray: DashboardDeviceData[] = Object.values(deviceWithData);
        return devicesArray;
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      return [];
    }
  }
  return [];
}

function categoryFormat(day: Dayjs, timeGroup: TimeGroupingInterval) {
  if (timeGroup === 'week') return 'W-' + day.week();
  const formatMap = {
    day: 'YYYY-MM-DD',
    month: 'YYYY-MM',
    year: 'YYYY',
  };
  return day.format(formatMap[timeGroup]);
}

function workingHoursBarchart(
  data: DashboardDeviceData[],
  timeInterval: TimeRange,
  timeGrouping: TimeGroupingInterval
): BarchartObject {
  // grouping the data by timeGrouping
  const groups = {} as { [timeGroup: string]: { inside: number; outside: number } };

  data.forEach((device) => {
    device.data.forEach((dataPoint) => {
      const timeGroup = dayjs(dataPoint.timestamp).startOf(timeGrouping).format('YYYY-MM-DD');

      if (groups[timeGroup] === undefined) {
        groups[timeGroup] = { inside: 0, outside: 0 };
      }
      const minutes = timestampToMinutes(dataPoint.timestamp);
      // Check if datapoint is in range
      if (minutes >= timeInterval[0] && minutes <= timeInterval[1]) {
        groups[timeGroup].inside += dataPoint.value;
      } else {
        groups[timeGroup].outside += dataPoint.value;
      }
    });
  });

  const keys = Object.keys(groups).sort((a, b) => dayjs(a).unix() - dayjs(b).unix());

  const series: SeriesData[] = [
    {
      name: 'Outside Working Hours',
      data: keys.map((k) => groups[k].outside),
      color: insideOutsideFills[1],
    },
    {
      name: 'Inside Working Hours',
      data: keys.map((k) => groups[k].inside),
      color: insideOutsideFills[0],
    },
  ];

  const categories = keys.map((k) => categoryFormat(dayjs(k), timeGrouping));

  return { categories, series } as BarchartObject;
}

function tagsBarchart(data: DashboardDeviceData[], timeGrouping: TimeGroupingInterval): BarchartObject {
  // grouping the data by date and tag
  const groups = {} as { [date: string]: { [tag: string]: number } };
  const tagSet = new Set<string>();

  data.forEach((device) => {
    device.data.forEach((dataPoint) => {
      const tag = device.tags[0] ?? 'Untagged';
      const date = dayjs(dataPoint.timestamp).startOf(timeGrouping).format('YYYY-MM-DD');

      if (groups[date] === undefined) {
        groups[date] = {};
      }

      if (groups[date][tag] === undefined) {
        groups[date][tag] = 0;
        tagSet.add(tag);
      }

      groups[date][tag] += dataPoint.value;
    });
  });

  const keys = Object.keys(groups).sort((a, b) => dayjs(a).unix() - dayjs(b).unix());
  const tags = Array.from(tagSet);

  const seriesData = tags.map((t, i) => {
    const series = { name: t, data: [] as number[], color: fills[i % fills.length] };
    keys.forEach((k) => {
      series.data.push(groups[k][t] ?? 0);
    });
    return series;
  });

  return { categories: keys.map((k) => categoryFormat(dayjs(k), timeGrouping)), series: seriesData };
}

export function getBarchartData(
  type: GroupBy,
  data: DashboardDeviceData[],
  timeInterval: TimeRange,
  timeGrouping: TimeGroupingInterval
): BarchartObject {
  if (type === 'Working Hours') {
    return workingHoursBarchart(data, timeInterval, timeGrouping);
  } else {
    return tagsBarchart(data, timeGrouping);
  }
}
